import { useInView } from '../../hooks/useInView';

const steps = [
  {
    number: '01',
    icon: 'ri-chat-voice-line',
    title: 'Initial Consultation',
    desc: 'A deep-dive conversation to understand your current state, goals, physical history, and areas of concern — so we can build the clearest possible picture of what your body truly needs.',
  },
  {
    number: '02',
    icon: 'ri-file-list-3-line',
    title: 'Personalized Treatment Plan',
    desc: 'A tailored therapeutic approach crafted specifically around your body, your history, and your healing objectives — never a generic protocol off a shelf.',
  },
  {
    number: '03',
    icon: 'ri-hand-heart-line',
    title: 'Hands-On Healing Session',
    desc: 'Targeted therapeutic work combining multiple modalities into one focused, high-impact 60–90 minute experience designed to produce real, measurable shifts.',
  },
  {
    number: '04',
    icon: 'ri-compass-3-line',
    title: 'Integration & Guidance',
    desc: 'Post-session recommendations to anchor your results, support your recovery between visits, and help you maintain the progress you\'ve made long after you leave.',
  },
];

interface ProcessSectionProps {
  variant?: 'light' | 'dark';
}

export default function ProcessSection({ variant = 'dark' }: ProcessSectionProps) {
  const { ref, inView } = useInView();
  const bg = variant === 'light' ? 'bg-[#0f0f0f]' : 'bg-[#0a0a0a]';

  return (
    <section
      id="process"
      ref={ref as React.RefObject<HTMLElement>}
      className={`${bg} py-32 border-t border-[#d4af37]/8`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        {/* Header */}
        <div className={`text-center mb-20 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="flex items-center justify-center gap-4 mb-5">
            <span className="w-12 h-px bg-[#d4af37]/30" />
            <p className="text-[#d4af37] uppercase tracking-[0.3em] text-[10px]" style={{ fontFamily: 'Inter, sans-serif' }}>
              How It Works
            </p>
            <span className="w-12 h-px bg-[#d4af37]/30" />
          </div>
          <h2
            className="text-4xl md:text-5xl lg:text-6xl text-white font-light"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            A Structured Approach to Healing
          </h2>
          <p className="text-[#5a5a5a] text-sm mt-5 max-w-md mx-auto" style={{ fontFamily: 'Inter, sans-serif' }}>
            Every session follows a deliberate, proven framework designed to maximize results.
          </p>
        </div>

        {/* Steps */}
        <div className="relative">
          {/* Connecting line */}
          <div className="hidden lg:block absolute top-[68px] left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-transparent via-[#d4af37]/20 to-transparent z-0" />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 relative z-10">
            {steps.map((step, idx) => (
              <div
                key={idx}
                className={`group relative bg-[#111111] border border-[#1e1e1e] hover:border-[#d4af37]/25 rounded-2xl p-8 transition-all duration-500 hover:-translate-y-1 ${
                  inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${200 + idx * 120}ms` }}
              >
                <div className="absolute top-5 right-6">
                  <span
                    className="text-5xl text-[#d4af37]/6 font-light select-none"
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  >
                    {step.number}
                  </span>
                </div>
                <div className="w-12 h-12 flex items-center justify-center mb-6 rounded-xl bg-[#d4af37]/8 group-hover:bg-[#d4af37]/15 transition-colors">
                  <i className={`${step.icon} text-[#d4af37] text-xl`} />
                </div>
                <h3
                  className="text-white text-xl font-light mb-3"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                >
                  {step.title}
                </h3>
                <p className="text-[#5a5a5a] text-sm leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {step.desc}
                </p>
                <div className="absolute bottom-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#d4af37]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
